import { getGlobal, plainToInstance } from 'class-transformer';
import { getMergedClassTransformOptions } from './global-dynamic-form-builder-options';
import { ClassTransformExecutor } from '@core/shared/model';
export var ClassTransformerMetadataHelpers;
(function (ClassTransformerMetadataHelpers) {
  function findMetadataForProperty(meta, name) {
    return meta.properties.find((metadataItem, index) => {
      return metadataItem.propertyName === name;
    });
  }
  ClassTransformerMetadataHelpers.findMetadataForProperty = findMetadataForProperty;
  function collectDiscriminatorSubtypeMetadata(metadataSource, classType) {
    // NOTE: according to metadata interfaces, exclude/expose metadata cannot have discriminator at all...
    const coll = Array.from(metadataSource.entries()).filter(([ctor, metas]) => {
      return ctor === classType;
    }).map(entry => {
      const meta = entry[1];
      return Array.from(meta.values()).filter(sub => {
        return !!sub.options['discriminator'];
      }).map(subWithDiscr => {
        return {
          ...subWithDiscr,
          ...subWithDiscr.options['discriminator']
        };
      });
    }).filter(arr => arr.length > 0);
    const merged = coll.reduce((all, subArray) => {
      subArray.forEach(metaEntry => all.push(buildSubtypeMetaEntry(metaEntry)));
      return all;
    }, []);
    return merged;
  }
  function buildSubtypeMetaEntry(metaEntry) {
    return {
      classType: metaEntry.target,
      propertyName: metaEntry.propertyName,
      types: metaEntry.subTypes || null,
      property: metaEntry.property
    };
  }
  // NOTE: leftover of ngx-dynamic-form-builder. 
  // this was supposed to do something for multitype/discriminator handling, but it is unknown what it actually does.
  // possibly the plainToInstance needs to be run for subtypes to populate some metadata / cache?
  function createDiscriminatorSubtypes(multiTypes, classTransformOptions) {
    multiTypes.forEach(multiType => {
      multiType.types.forEach(type => {
        const obj = {
          [multiType.propertyName]: {
            [multiType.property]: type.name
          }
        };
        plainToInstance(multiType.classType, obj, classTransformOptions);
      });
    });
  }
  // custom: getMetadata args have been changed. Before optional parameters, these were added:
  // dynamicFormBuilderOptions
  // currentDepth
  // propertyPath
  function getMetadata(classType, dynamicFormBuilderOptions, currentDepth, currentPath, isRoot) {
    const classTransformerMetadataStorage = ClassTransformExecutor.getMetadataStorage();
    const classValidatorMetadataStorage = getGlobal()['classValidatorMetadataStorage'] || undefined;
    if (!classValidatorMetadataStorage) throw new Error('classValidatorMetadataStorage not set in windows');
    const classTransformOptions = getMergedClassTransformOptions(dynamicFormBuilderOptions);
    let exposeMetaWithAncestors = [];
    let excludeMetaWithAncestors = [];
    let prevMultiTypes = [];
    let multiTypes = null;
    if (isRoot) {
      while (!prevMultiTypes || !multiTypes || prevMultiTypes.length !== multiTypes.length) {
        prevMultiTypes = multiTypes;
        plainToInstance(classType, {}, classTransformOptions);
        // TODO: why does this collect type metadata, while in second loop it uses excludeMetadata?
        multiTypes = collectDiscriminatorSubtypeMetadata(classTransformerMetadataStorage._typeMetadatas, classType);
        createDiscriminatorSubtypes(multiTypes, classTransformOptions);
        exposeMetaWithAncestors = classTransformerMetadataStorage.getExposedMetadatas(classType);
      }
      prevMultiTypes = [];
      multiTypes = null;
      while (!prevMultiTypes || !multiTypes || prevMultiTypes.length !== multiTypes.length) {
        prevMultiTypes = multiTypes;
        plainToInstance(classType, {}, classTransformOptions);
        multiTypes = collectDiscriminatorSubtypeMetadata(classTransformerMetadataStorage._excludeMetadatas, classType);
        createDiscriminatorSubtypes(multiTypes, classTransformOptions);
        excludeMetaWithAncestors = classTransformerMetadataStorage.getExcludedMetadatas(classType);
      }
    } else {
      exposeMetaWithAncestors = classTransformerMetadataStorage.getExposedMetadatas(classType);
      excludeMetaWithAncestors = classTransformerMetadataStorage.getExcludedMetadatas(classType);
    }
    /**
     * NOTE: Ancestors ARE in use in some places.
     * Known: QuestionHitsByCategoryQueryParams
     * However, we were not able to figure out when ancestors will be detected and when not.
     */
    const ancestorsProperties = [Array.from(classTransformerMetadataStorage._ancestorsMap).filter(item => item[1][0] === classType).map(item => classTransformerMetadataStorage.getExposedMetadatas(item[0])).reduce((all, cur) => [...all, ...cur], [])];
    if (ancestorsProperties.length > 0 && ancestorsProperties[0].length > 0) {
      console.warn('Detected ancestorsProperties', classType, ancestorsProperties);
      // throw new Error('Detected ancestorsProperties. These have been removed from dynamic-form-group prop evaluation. If used anywhere, the implementation may need to be refined or checked.')
    }
    const excludeGroups = classTransformOptions?.excludeGroups || [];
    const metaForPropsToCreate = [...exposeMetaWithAncestors
    // ...excludeProperties,
    // ...(ancestorsProperties.length > 0 ? ancestorsProperties[0] : []),
    ].filter((current, index, allProps) => {
      // ensure there's no exclude metadata overriding exposure
      const propExcludeMeta = excludeMetaWithAncestors.find(excludeMeta => excludeMeta.propertyName === current.propertyName);
      if (propExcludeMeta && !propExcludeMeta.options?.toClassOnly && !propExcludeMeta.options?.toPlainOnly) return false;
      // ensure there's no exclude group overriding exposure
      const propIsExcludedByGroup = excludeGroups.find(excludeGroup => current?.options?.groups?.includes(excludeGroup));
      if (propIsExcludedByGroup) return false;
      // ensure there is no entry with higher priority than the current one
      const firstIndexForProperty = allProps.findIndex(it => it.propertyName === current.propertyName);
      if (index !== firstIndexForProperty) return false;
      return true;
    });
    // console.log('===== DFB: calculated properties to expose', classType, {
    // 	exposeProperties: exposeMetaWithAncestors, 
    // 	excludeProperties: excludeMetaWithAncestors, 
    // 	ancestorsProperties,
    // 	result:objectCloneDeep(metaForPropsToCreate), 
    // })
    // NOTE: this next section MUTATES properties and searches for property types.
    // when not deep cloning in the above log, it looks like the properties would already contain type metadata!
    const normalizedProperties = collectControlMetadata(metaForPropsToCreate, classType, currentPath, dynamicFormBuilderOptions, currentDepth);
    const metadata = {
      classType,
      properties: normalizedProperties,
      withAncestors: (ancestorsProperties?.length > 0 ? ancestorsProperties[0] : []).length > 0,
      isArray: false,
      propertyName: null
    };
    return metadata;
  }
  ClassTransformerMetadataHelpers.getMetadata = getMetadata;
  function collectControlMetadata(metaForPropsToCreate, classType, currentPath, dynamicFormBuilderOptions, currentDepth) {
    const normalizedProperties = [];
    const depthLimitExceeded = currentDepth >= dynamicFormBuilderOptions.maxNestedModelDepth;
    const classTransformerMetadataStorage = ClassTransformExecutor.getMetadataStorage();
    metaForPropsToCreate.forEach((exposeMetadataItem, index) => {
      // WARNING: exposeMetadataItem.target may point to a parent class. It must NOT BE USED!
      const propertyTypeMeta = classTransformerMetadataStorage.findTypeMetadata(classType, exposeMetadataItem.propertyName);
      if (propertyTypeMeta) {
        // console.log('>>>>> classTransformerMetadataStorage found type metadata for ' + exposeMetadataItem.propertyName, propertyTypeMeta);
        // custom: skip if depthLimitExceeded
        if (depthLimitExceeded) {
          console.warn('dynamic-form-builder has hit depth limit!', propertyTypeMeta);
          // delete properties[index];
        } else {
          const propertyPath = [...currentPath, exposeMetadataItem.propertyName];
          // custom: this is new logic. Instead of assigning all, 
          // metadata will be filtered out if allowedNestedModels are defined and do not include this prop.
          const allowAllNestedModels = !Array.isArray(dynamicFormBuilderOptions.allowedNestedModels);
          const isAllowedNestedModel = allowAllNestedModels || dynamicFormBuilderOptions.allowedNestedModels.includes(propertyPath.join('.'));
          if (isAllowedNestedModel) {
            // meta.typeFunction is used to get the actual ctor for the class.
            // meta.reflectedType helps to determine if the expected structure is an array or an object.
            const isArray = propertyTypeMeta.reflectedType === Array;
            normalizedProperties.push({
              ...getMetadata(propertyTypeMeta.typeFunction(), dynamicFormBuilderOptions, currentDepth + 1, propertyPath, currentDepth === 0),
              isArray,
              propertyName: exposeMetadataItem.propertyName
            });
          }
        }
      } else {
        normalizedProperties.push({
          classType: null,
          properties: [],
          isArray: false,
          propertyName: exposeMetadataItem.propertyName
        });
      }
    });
    return normalizedProperties;
  }
})(ClassTransformerMetadataHelpers || (ClassTransformerMetadataHelpers = {}));